'use client';

import { EMAIL_INPUT_PATTERN, formatEmail } from '@/components/Form/EmailInput';
import { subscribeMarketingPage, unsubscribeMarketingPage } from '@/api/marketingPages';
import Image from '@/components/Image';
import { toast } from '@/components/Toast';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { relationEmailMaxLength } from '@/utils/tools';
import { useState, type FormEvent } from 'react';
import type { CapsuleSubscriptionData, MarketingPageTemplateType } from '../../../types';
import { resolveCapsuleAssetUrl } from '../utils';

interface CapsuleSubscriptionProps {
  data: CapsuleSubscriptionData;
  activityType: MarketingPageTemplateType;
  isWap?: boolean;
  marketingPageId: number;
  onButtonClick?: (buttonName: CapsuleSubscriptionButtonName) => void;
  onModuleExposure?: () => void;
}

export type CapsuleSubscriptionButtonName = 'subscribe' | 'Unsubscribe';

export function formatCapsuleSubscriptionDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getSubscriptionResponseMessage(error: unknown): string | undefined {
  if (isRecord(error)) {
    const response = error.response;

    if (isRecord(response) && isRecord(response.data)) {
      const responseMessage = response.data.msg;

      if (typeof responseMessage === 'string' && responseMessage) {
        return responseMessage;
      }
    }

    if (typeof error.msg === 'string' && error.msg) {
      return error.msg;
    }
  }

  return error instanceof Error && error.message ? error.message : undefined;
}

export default function CapsuleSubscription({
  data,
  activityType,
  isWap = false,
  marketingPageId,
  onButtonClick,
  onModuleExposure,
}: CapsuleSubscriptionProps) {
  const tCapsule = useTranslations('activity.campaigns.capsule');
  const tEmail = useTranslations('components.form.email');
  const userEmail = useStore((state) => state.userInfo.email || '');
  const [email, setEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputEmail = email ?? formatEmail(userEmail);
  const image = resolveCapsuleAssetUrl(isWap ? data.wap_image || data.pc_image : data.pc_image);
  const { ref: moduleExposureRef } = useValidExposure({
    callback: () => onModuleExposure?.(),
    ts: `${marketingPageId}-${isWap ? 'wap' : 'web'}-${image}`,
  });

  if (!image || !data.button_text) return null;

  const handleSubscribe = async () => {
    if (isSubmitting) return;

    const submitEmail = inputEmail.trim();
    if (!EMAIL_INPUT_PATTERN.test(submitEmail)) {
      toast.error(tEmail('validation.invalid'));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await subscribeMarketingPage({
        email: submitEmail,
        activity_type: activityType,
        subscribed_periodical_date: formatCapsuleSubscriptionDate(new Date()),
        marketing_page_id: marketingPageId,
      });

      setEmail(submitEmail);
      toast.success(result.msg);
    } catch (error: unknown) {
      const message = getSubscriptionResponseMessage(error);

      if (message) toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSubscribe();
  };

  const handleUnsubscribe = async () => {
    if (isSubmitting) return;

    const submitEmail = inputEmail.trim();
    if (!EMAIL_INPUT_PATTERN.test(submitEmail)) {
      toast.error(tEmail('validation.invalid'));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await unsubscribeMarketingPage({
        email: submitEmail,
        activity_type: activityType,
        marketing_page_id: marketingPageId,
      });

      setEmail(submitEmail);
      toast.success(result.msg);
    } catch (error: unknown) {
      const message = getSubscriptionResponseMessage(error);

      if (message) toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      ref={moduleExposureRef}
      className={
        isWap
          ? 'w-full'
          : 'mx-auto w-[calc(100%_-_96px)] max-w-[1824px] pb-[40px] pt-0 min-[1025px]:pb-[60px] min-[1281px]:pb-[70px] min-[1441px]:pb-[80px]'
      }
    >
      <div
        className={
          isWap
            ? 'flex w-full flex-col overflow-hidden'
            : 'flex aspect-[1824/400] w-full overflow-hidden'
        }
      >
        <div
          className={
            isWap
              ? 'relative h-[200px] w-full shrink-0 overflow-hidden'
              : 'relative w-1/2 shrink-0 overflow-hidden'
          }
        >
          <Image
            src={image}
            alt={data.title || ''}
            fill
            sizes={isWap ? '100vw' : '50vw'}
            className={`object-cover ${isWap ? 'object-top' : 'object-center'}`}
          />
        </div>
        <div
          className={
            isWap
              ? 'flex w-full shrink-0 bg-[#f4f4f4] px-[12px] py-[24px]'
              : 'flex w-1/2 shrink-0 items-center bg-[#f4f4f4] px-[32px] py-[24px] min-[1920px]:px-[60px] min-[1920px]:py-[64px]'
          }
        >
          <div
            className={`flex w-full flex-col ${isWap ? 'gap-[16px]' : 'gap-[20px] min-[1920px]:gap-[28px]'}`}
          >
            <div
              className={`flex flex-col ${isWap ? 'items-center gap-[8px] text-center' : 'gap-[8px] min-[1920px]:gap-[12px]'}`}
            >
              {data.title ? (
                <h2
                  className={`font-bb font-bold text-[#282828] ${isWap ? 'text-[16px] leading-[normal]' : 'text-[16px] leading-normal min-[1920px]:text-[24px]'}`}
                >
                  {data.title}
                </h2>
              ) : null}
              {data.sub_title ? (
                <p
                  className={`font-br ${isWap ? 'text-[12px] leading-[normal] text-[#6c6c6c]' : 'text-[12px] leading-normal text-[#6c6c6c] min-[1920px]:text-[14px] min-[1920px]:leading-[17px] min-[1920px]:text-secondary-text'}`}
                >
                  {data.sub_title}
                </p>
              ) : null}
            </div>
            <div
              className={`flex flex-col items-center ${isWap ? 'gap-[12px]' : 'gap-[12px] min-[1920px]:gap-[16px]'}`}
            >
              <form
                className={`relative flex w-full items-center ${isWap ? 'h-[50px]' : 'h-[36px] min-[1920px]:h-[52px]'}`}
                onSubmit={handleSubmit}
              >
                <input
                  type="email"
                  value={inputEmail}
                  disabled={isSubmitting}
                  maxLength={50 + relationEmailMaxLength}
                  autoComplete="email"
                  aria-label={tEmail('label')}
                  placeholder={tCapsule('emailPlaceholder')}
                  className={
                    isWap
                      ? 'h-full w-full rounded-[6px] border border-[#d1d9e0] bg-white px-[12px] pr-[116px] font-br text-[14px] text-primary-text outline-none placeholder:text-secondary-text focus:border-primary-purple disabled:cursor-not-allowed disabled:text-secondary-text'
                      : 'h-full w-full rounded-[4px] border border-[#d1d9e0] bg-white px-[8px] pr-[88px] font-br text-[12px] text-primary-text outline-none placeholder:text-[#6c6c6c] focus:border-primary-purple disabled:cursor-not-allowed disabled:text-secondary-text min-[1920px]:rounded-[6px] min-[1920px]:px-[12px] min-[1920px]:pr-[116px] min-[1920px]:text-[14px] min-[1920px]:placeholder:text-secondary-text'
                  }
                  onChange={(event) => setEmail(formatEmail(event.target.value))}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={
                    isWap
                      ? 'absolute right-[5px] top-1/2 h-[38px] w-[96px] -translate-y-1/2 rounded-[4px] px-[16px] font-br text-[12px] leading-[14px] text-white disabled:cursor-not-allowed disabled:opacity-60'
                      : 'absolute right-[3px] top-1/2 h-[25px] w-[80px] -translate-y-1/2 rounded-[3px] px-[8px] font-br text-[12px] leading-normal text-white disabled:cursor-not-allowed disabled:opacity-60 min-[1920px]:right-[5px] min-[1920px]:h-[38px] min-[1920px]:min-w-[96px] min-[1920px]:rounded-[4px] min-[1920px]:px-[16px] min-[1920px]:leading-[14px]'
                  }
                  style={{ backgroundColor: data.button_color || '#463ae8' }}
                  onClick={() => onButtonClick?.('subscribe')}
                >
                  {data.button_text}
                </button>
              </form>
              <button
                type="button"
                disabled={isSubmitting}
                className={`font-br text-[12px] underline disabled:cursor-not-allowed disabled:opacity-60 ${isWap ? 'leading-[14px] text-secondary-text' : 'leading-normal text-[#6c6c6c] min-[1920px]:text-secondary-text'}`}
                onClick={() => {
                  onButtonClick?.('Unsubscribe');
                  void handleUnsubscribe();
                }}
              >
                {tCapsule('unsubscribe')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
